import { useEffect, useState } from "react";

/**
 * Hook that tracks whether a CSS media query matches.
 * Uses the MediaQueryList API for efficient change detection.
 *
 * @param query - CSS media query string (e.g., "(min-width: 1024px)")
 * @returns boolean indicating if the media query currently matches
 *
 * @example
 * // Check if screen is at least large (1024px)
 * const isLargeScreen = useMediaQuery("(min-width: 1024px)");
 *
 * @example
 * // Check for dark mode preference
 * const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(false);

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);

    mql.addEventListener("change", onChange);
    setMatches(mql.matches); // Set initial value

    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}
